import React, { useState, useEffect, useMemo } from 'react';
import { signOut as nextAuthSignOut, useSession } from 'next-auth/react';
import { ThemeToggle } from './theme-toggle';
import { Button } from '@/components/ui/button';
import { Sheet } from '@/components/ui/sheet';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/hooks/useAuth';
import {
  Package,
  Plus,
  Search,
  Filter,
  Bell,
  User,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Boxes,
  Tags,
  Palette,
  Ruler,
  Building2,
  UserCheck,
  Package2,
  Percent,
  Star,
  TrendingUp,
  Truck,
  BarChart3,
  Archive,
  Menu,
  X,
  Home,
  ShoppingCart,
  Users,
  Settings,
  Warehouse
} from 'lucide-react';
import { navigationService } from '@/shared/navigation';
import { UserRole, getUserRoleFromString } from '@/shared/permissions';
import { OrgType, DEFAULT_ORG_TYPE } from '../../lib/types/org';
import { toast } from "sonner";

// Icon mapping for navigation - maps string names to React components
const iconMap: Record<string, React.ElementType> = {
  Home: Home,
  Package: Package,
  Archive: Archive,
  ShoppingCart: ShoppingCart,
  Users: Users,
  BarChart3: BarChart3,
  Settings: Settings,
  Warehouse: Warehouse,
  Boxes: Boxes,
  Tags: Tags,
  Palette: Palette,
  Ruler: Ruler,
  Building2: Building2,
  UserCheck: UserCheck,
  Package2: Package2,
  Percent: Percent,
  Star: Star,
  TrendingUp: TrendingUp,
  Truck: Truck,
  Menu: Menu,
  X: X,
  Plus: Plus,
  Search: Search,
  Filter: Filter,
  Bell: Bell,
  User: User,
  ChevronDown: ChevronDown,
  ChevronLeft: ChevronLeft,
  ChevronRight: ChevronRight
};

// UI primitives from shadcn/ui should be used for all common elements
// For demonstration, using button/input directly, but replace with shadcn/ui Button/Input if available in /components/ui/

interface HeaderProps {
  children?: React.ReactNode;
}

const Header: React.FC<HeaderProps> = ({ children }) => {
  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  const pathname = usePathname();
  const router = useRouter();
  const { data: session, status } = useSession();
  const { 
    orgId, 
    userId, 
    orgType, 
    orgName, 
    role, 
    isLoading, 
    isEnhanced,
    error,
    retry
  } = useAuth();
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [activeModule, setActiveModule] = useState<string | null>(null);
  const [activeSubMenu, setActiveSubMenu] = useState('overview');
  const [isNavigating, setIsNavigating] = useState(false);

  // menuGroups must be available before effects that use it - calculated with current orgType or default
  // Get navigation data using the new consolidated system
  const [navigationData, setNavigationData] = useState<{
    leftMenuGroups: Array<{
      group: string;
      items: Array<{
        id: string;
        label: string;
        path: string;
        icon?: string;
      }>;
    }>;
    topMenus: Record<string, Array<{
      id: string;
      label: string;
      path: string;
    }>>;
    source: 'static' | 'database';
  } | null>(null);
  
  const [navigationLoading, setNavigationLoading] = useState(true);
  const [navigationError, setNavigationError] = useState<string | null>(null);

  // Load navigation data
  useEffect(() => {
    const loadNavigation = async () => {
      try {
        setNavigationLoading(true);
        setNavigationError(null);
        
        // Parse user role
        const userRole = getUserRoleFromString(role);
        if (!userRole) {
          throw new Error('Invalid user role');
        }

        // Get static navigation (default mode)
        const navigation = navigationService.getStaticNavigation(userRole, 'manufacturer');
        setNavigationData(navigation);
        
      } catch (error) {
        console.error('Header: Failed to load navigation:', error);
        setNavigationError(error instanceof Error ? error.message : 'Unknown error');
      } finally {
        setNavigationLoading(false);
      }
    };

    if (role) {
      loadNavigation();
    } else {
      console.log('Header: No role available, skipping navigation load');
    }
  }, [role]);

  const menuGroups = navigationData?.leftMenuGroups || [];
  const topMenus = navigationData?.topMenus || {};
  const navigationSource = navigationData?.source || 'static';
  const isAuthPage = pathname.startsWith('/login') || pathname.startsWith('/auth');

  // Handle redirects (must be called before any conditional returns)
  useEffect(() => {
    if (status === 'unauthenticated' && !isAuthPage) {
      router.replace('/login');
    }
  }, [status, isAuthPage]);
  
  // If on auth page, render children directly without any auth logic
  if (isAuthPage) {
    return <>{children}</>;
  }

  // Update activeModule based on current pathname
  useEffect(() => {
    // Find best matching menu item for current path (longest prefix wins).
    // If nothing matches, fall back to the first visible menu item (or keep existing selection).
    let matched: string | null = null;
    let bestLen = -1;

    for (const group of menuGroups) {
      for (const item of group.items) {
        if (pathname.startsWith(item.path)) {
          const len = item.path.length;
          if (len > bestLen) {
            bestLen = len;
            matched = item.id;
          }
        }
      }
    }

    if (matched) {
      setActiveModule(matched);
    } else {
      const fallback = menuGroups[0]?.items[0]?.id ?? 'products';
      // only set fallback if there's no current selection, preserve user choice otherwise
      setActiveModule((prev) => prev ?? fallback);
    }

    // Debug: Log navigation source
    if (navigationSource) {
      console.log('Header: Using navigation source:', navigationSource);
    }
  }, [pathname, menuGroups, navigationSource]);

  // Content icons for different product sub-sections
  const productIcons: Record<string, React.ElementType> = {
    overview: BarChart3,
    catalog: Package,
    variants: Package2,
    categories: Archive,
    brands: Building2,
    attributes: Tags,
    pricing: Percent,
    inventory: Boxes
  };

  const handleModuleChange = (moduleId: string) => {
    if (moduleId === 'categories') {
      setActiveModule('products');
      setActiveSubMenu('categories');
    } else {
      setActiveModule(moduleId);
      setActiveSubMenu('overview'); // Reset to overview when changing modules
    }
    setIsMobileMenuOpen(false); // Close mobile menu on selection
  };

  // Navigation handler for smooth client-side navigation
  const handleNavigation = async (item: any, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation(); // Stop event bubbling
    }
    
    if (activeModule === item.id) return; // Don't navigate if already active
    
    console.log('Header: Starting navigation to', { 
      itemId: item.id, 
      itemPath: item.path, 
      currentActiveModule: activeModule 
    });
    
    setIsNavigating(true);
    
    try {
      // Update UI immediately for responsive feel
      handleModuleChange(item.id);
      
      // Prefetch the route for faster loading
      router.prefetch(item.path);
      
      // Small delay to ensure state updates are processed
      await new Promise(resolve => setTimeout(resolve, 0));
      
      // Use router.push for client-side navigation
      router.push(item.path);
      
      console.log('Header: Navigation completed to', item.path);
      
    } catch (error) {
      console.error('Navigation error:', error);
      toast.error('Navigation failed. Please try again.');
    } finally {
      // Small delay before resetting navigation state
      setTimeout(() => setIsNavigating(false), 100);
    }
  };

  // Sub-menu navigation handler
  const handleSubMenuNavigation = async (subItem: any, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation(); // Stop event bubbling
    }
    
    if (activeSubMenu === subItem.id) return; // Don't navigate if already active
    
    console.log('Header: Starting submenu navigation to', { 
      subItemId: subItem.id, 
      subItemPath: subItem.path, 
      currentActiveSubMenu: activeSubMenu 
    });
    
    setIsNavigating(true);
    
    try {
      // Update UI immediately
      setActiveSubMenu(subItem.id);
      
      // Navigate without prefetch
      await router.push(subItem.path);
      
      console.log('Header: Submenu navigation completed to', subItem.path);
      
    } catch (error) {
      console.error('Sub-menu navigation error:', error);
      toast.error('Navigation failed. Please try again.');
    } finally {
      setIsNavigating(false);
    }
  };

  // Sidebar content component (reusable for both desktop and mobile)
  const SidebarContent = React.memo(({ isMobile = false }: { isMobile?: boolean }) => {
    return (
      <div className={`${isMobile ? 'h-full' : 'h-screen'} bg-gradient-to-b from-teal-600 to-teal-500 text-white flex flex-col shadow-lg`}>
        {/* Logo + collapse toggle (desktop) */}
        <div className={`h-16 flex items-center justify-between border-b border-teal-700 ${isSidebarCollapsed ? 'px-3' : 'px-6'}`}>
          <div className={`flex items-center gap-3 ${isSidebarCollapsed ? 'justify-center w-full' : ''}`}>
            <div className={`w-9 h-9 bg-white/20 backdrop-blur-sm ${isSidebarCollapsed ? 'rounded-lg' : 'rounded-xl'} flex items-center justify-center border border-white/10 shadow-sm`}>
              <Package className="w-5 h-5 text-white" />
            </div>
            {!isSidebarCollapsed && (
              <div className="flex flex-col">
                <span className="text-xl font-bold text-white leading-tight">ERP</span>
                <span className="text-xs text-teal-100 font-medium">Management System</span>
              </div>
            )}
          </div>
          {isMobile && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-white hover:bg-teal-700"
            >
              <X className="w-5 h-5" />
            </Button>
          )}
        </div>

        {/* Navigation Menu */}
        <nav className={`flex-1 overflow-y-auto ${isSidebarCollapsed ? 'py-4' : 'p-4'}`}>
          {/* Development: Show navigation source */}
          {process.env.NODE_ENV === 'development' && navigationSource && !isSidebarCollapsed && (
            <div className="mb-4 px-2">
              <div className={`text-xs px-2 py-1 rounded text-center ${
                navigationSource === 'static' 
                  ? 'bg-green-700 bg-opacity-30 text-green-200' 
                  : 'bg-blue-700 bg-opacity-30 text-blue-200'
              }`}>
                {navigationSource === 'static' ? '🚀 Static Navigation' : '🗄️ Database Navigation'}
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            {navigationLoading && (
              <div className="text-teal-200 text-sm px-2">Loading navigation...</div>
            )}
            {navigationError && (
              <div className="text-red-400 text-sm px-2">Error: {navigationError}</div>
            )}
            {menuGroups.map((group) => (
              <div key={group.group}>
                {!isSidebarCollapsed && (
                  <div className="text-xs font-semibold text-teal-200 mb-2 px-2 uppercase tracking-wide">
                    {group.group}
                  </div>
                )}
                <div className="space-y-1">
                  {group.items.map((item) => {
                    const Icon = iconMap[item.icon || 'Package'];
                    const isActive = activeModule === item.id;
                    const commonClass = `w-full rounded-lg text-left transition-colors ${
                      isActive
                        ? (isSidebarCollapsed ? 'bg-teal-700 bg-opacity-30 text-white' : 'bg-teal-700 bg-opacity-30 text-white border border-teal-300')
                        : 'text-teal-100 hover:bg-teal-700 hover:bg-opacity-20 hover:text-white'
                    } ${isSidebarCollapsed ? 'flex flex-col items-center justify-center px-1 py-2' : 'flex items-center gap-3 px-3 py-2.5'}
                    ${isNavigating ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`;

                    return (
                      <button
                        type="button"
                        key={item.id}
                        onClick={(e) => handleNavigation(item, e)}
                        onMouseEnter={() => router.prefetch(item.path)} // Prefetch on hover
                        disabled={isNavigating}
                        title={item.label}
                        className={commonClass}
                      >
                        {isSidebarCollapsed ? (
                          <div className="flex flex-col items-center">
                            <div className={`flex items-center justify-center ${isActive ? 'border border-teal-300 rounded-md p-1' : ''}`}>
                              {Icon && <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-teal-200'}`} />}
                            </div>
                            <span className={`text-xs mt-1 ${isActive ? 'text-white' : 'text-teal-100'}`}>{item.label}</span>
                          </div>
                        ) : (
                          <>
                            {Icon && <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-teal-200'}`} />}
                            <span className="font-medium">{item.label}</span>
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </nav>

        {/* User Profile */}
        <div className={`border-t border-teal-700 ${isSidebarCollapsed ? 'py-3' : 'p-4'}`}>
          <div title={isSidebarCollapsed ? `${session?.user?.name || session?.user?.email || 'User'}${role ? ' · ' + role : ''}` : undefined} className={`flex items-center gap-3 ${isSidebarCollapsed ? 'justify-center' : ''} p-3 rounded-lg cursor-pointer transition-colors hover:bg-teal-700 hover:bg-opacity-30`}>
            <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-white" />
            </div>
            {!isSidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{session?.user?.name || session?.user?.email || 'User'}</p>
                {role && (
                  <p className="text-xs text-teal-200 truncate">{role}</p>
                )}
              </div>
            )}
            {!isSidebarCollapsed && <ChevronDown className="w-4 h-4 text-teal-200" />}
          </div>
        </div>
      </div>
    );
  });

  // Show loading state while authentication is being resolved
  if (isLoading) {
    return (
      <div className="h-screen flex">
        {/* Static sidebar placeholder during loading */}
        <div className="hidden lg:block w-16 bg-gradient-to-b from-teal-600 to-teal-500">
          <div className="h-16 flex items-center justify-center border-b border-teal-700">
            <div className="w-8 h-8 bg-white bg-opacity-20 rounded-md flex items-center justify-center">
              <Package className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
        
        {/* Main content area with loading */}
        <div className="flex-1 flex flex-col">
          {/* Top bar placeholder */}
          <div className="bg-gradient-to-r from-teal-600 to-teal-500 border-b border-teal-700 h-16"></div>
          
          {/* Loading content */}
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-teal-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If we have a session but no business context, show loading state with retry option
  if (session && !isEnhanced && !error?.includes('Not authenticated')) {
    console.log('Header: Session exists but no business context', {
      session: !!session,
      isEnhanced,
      error,
      isLoading,
      navigationLoading,
      navigationError,
      orgId,
      userId,
      orgType,
      timestamp: new Date().toISOString()
    });

    // Show loading state with stable layout
    return (
      <div className="h-screen flex">
        {/* Static sidebar during business context loading */}
        <div className="hidden lg:block w-16 bg-gradient-to-b from-teal-600 to-teal-500">
          <div className="h-16 flex items-center justify-center border-b border-teal-700">
            <div className="w-8 h-8 bg-white bg-opacity-20 rounded-md flex items-center justify-center">
              <Package className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
        
        <div className="flex-1 flex flex-col">
          {/* Top bar placeholder */}
          <div className="bg-gradient-to-r from-teal-600 to-teal-500 border-b border-teal-700 h-16"></div>
          
          {/* Business context loading */}
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-teal-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading organization context...</p>
              <p className="mt-2 text-sm text-gray-500">Please wait while we set up your session</p>
              {error && (
                <div className="mt-4">
                  <p className="text-red-600 text-sm mb-2">{error}</p>
                  {error.includes('No valid session found') ? (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500">Auto-retrying in a moment...</p>
                      <Button 
                        onClick={retry} 
                        variant="outline"
                        size="sm"
                        className="border-teal-600 text-teal-600 hover:bg-teal-50"
                      >
                        Retry Now
                      </Button>
                    </div>
                  ) : (
                    <Button 
                      onClick={retry} 
                      variant="outline"
                      className="border-teal-600 text-teal-600 hover:bg-teal-50"
                    >
                      Retry
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if there's a real authentication error
  if (error && (error.includes('Not authenticated') || 
                error.includes('Session validation failed') || 
                error.includes('Session expired') ||
                error.includes('Invalid session') ||
                error.includes('User not authenticated') ||
                error.includes('Please log in again'))) {
    
    // Auto-redirect for session/auth errors
    React.useEffect(() => {
      const timer = setTimeout(() => {
        router.replace('/login?reason=session_expired');
      }, 2000); // 2 second delay to show the message
      
      return () => clearTimeout(timer);
    }, []);

    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <p className="text-red-600 font-medium">
            {error.includes('Session validation failed') || error.includes('Session expired') || error.includes('Invalid session') 
              ? 'Session Expired' : 'Authentication Required'}
          </p>
          <p className="text-gray-600 text-sm mt-2">
            {error.includes('Session') ? 'Your session has expired. You will be redirected to login.' : error}
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
              <span className="text-sm text-gray-500">Redirecting to login...</span>
            </div>
            <Button 
              onClick={() => router.replace('/login?reason=session_expired')} 
              variant="outline"
              className="border-teal-600 text-teal-600 hover:bg-teal-50"
            >
              Go to Login Now
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    if (activeModule === 'products') {
      const IconComponent = productIcons[activeSubMenu] || Package;
      return (
        <div className="p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-brand-100 rounded-xl">
              <IconComponent className="w-6 h-6 text-brand-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {topMenus.products?.find((item: {id: string; label: string; path: string}) => item.id === activeSubMenu)?.label}
              </h1>
              <p className="text-gray-600">Manage your product {activeSubMenu}</p>
            </div>
          </div>
          {/* ...existing code for overview, catalog, variants, etc... */}
        </div>
      );
    }
    // Default content for other modules
    const allMenuItems = menuGroups.flatMap((group: any) => group.items);
    return (
      <div className="p-8">
        <div className="text-center py-12 text-gray-500">
          <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>{allMenuItems.find((item: any) => item.id === activeModule)?.label} module content coming soon</p>
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen content-background flex">
      {/* Desktop Sidebar - Hidden on mobile */}
      <div className={`hidden lg:block relative transition-all duration-300 ${
        isSidebarCollapsed ? 'w-16' : 'w-42'
      }`}>
        <SidebarContent />
        {/* Vertical collapse/expand handle for desktop */}
        <button
          aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          className="hidden lg:flex items-center justify-center absolute -right-3 top-1/2 transform -translate-y-1/2 w-6 h-10 bg-teal-50 border border-teal-200 rounded-md shadow-sm text-teal-600 hover:bg-teal-100 hover:border-teal-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-opacity-50 transition-all duration-200"
        >
          {isSidebarCollapsed ? (
            <ChevronRight className="w-3.5 h-3.5" />
          ) : (
            <ChevronLeft className="w-3.5 h-3.5" />
          )}
        </button>
      </div>

      {/* Mobile Sidebar using Sheet */}
      <Sheet open={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)}>
        <SidebarContent isMobile={true} />
      </Sheet>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Navigation Bar */}
        {/* Top Navigation Bar */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-500 border-b border-teal-700 h-16">
          <div className="flex items-center justify-between px-4 lg:px-6 h-full">
            {/* Left side - Mobile menu and breadcrumbs */}
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden text-white hover:bg-teal-700"
                onClick={() => setIsMobileMenuOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </Button>

              {/* Sub-navigation - Horizontal scroll on mobile */}
              <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
                {
                  // activeModule can be null while initializing — use a safe string key
                  (() => {
                    const activeKey = activeModule ?? menuGroups[0]?.items[0]?.id ?? 'products';
                    return (topMenus as Record<string, {id: string; label: string; path: string}[]>)[activeKey]?.map((subItem: {id: string; label: string; path: string}) => (
                      <button 
                        type="button"
                        key={subItem.id}
                        onClick={(e) => handleSubMenuNavigation(subItem, e)}
                        disabled={isNavigating}
                        className={`px-3 lg:px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                          activeSubMenu === subItem.id
                            ? 'bg-teal-700 bg-opacity-30 text-white'
                            : 'text-teal-100 hover:bg-teal-700 hover:bg-opacity-20 hover:text-white'
                        } ${isNavigating ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                      >
                        {subItem.label}
                      </button>
                    ));
                  })()
                }
              </div>
            </div>

            {/* Right side actions */}
            <div className="flex items-center gap-2 lg:gap-4">
              {/* Organization info - Hidden on small screens */}
              <div className="hidden md:flex items-center gap-4">
                <div className="w-px h-6 bg-teal-300"></div>
                <div className="flex items-center gap-3 px-3 py-2 bg-teal-700 rounded-lg">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div className="text-sm text-white">
                    <div className="font-medium text-white">
                      {orgName || 'Organization'}
                    </div>
                    <div className="text-xs text-teal-200">
                      {session?.user?.name || session?.user?.email || 'User'} {role && `· ${role}`}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="w-px h-6 bg-teal-300"></div>
              <Button
                type="button"
                onClick={async () => {
                  try {
                    // Clear all session storage first
                    if (typeof window !== 'undefined') {
                      window.sessionStorage.clear();
                      
                      // Clear all cookies that might be related to authentication
                      const cookies = document.cookie.split(';');
                      cookies.forEach(cookie => {
                        const [name] = cookie.trim().split('=');
                        // Clear all cookies by setting them to expire
                        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
                        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
                        // Also try with www subdomain
                        if (window.location.hostname.startsWith('www.')) {
                          const domain = window.location.hostname.substring(4);
                          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
                        }
                      });
                    }
                    
                    // Use server-side logout API which properly clears NextAuth session
                    const response = await fetch('/api/auth/logout', {
                      method: 'GET',
                      credentials: 'include', // Include cookies
                    });
                    
                    if (response.ok) {
                      // Server-side logout successful, redirect to login
                      window.location.href = '/login';
                    } else {
                      console.error('Server logout failed, falling back to client logout');
                      // Fallback to client-side signOut
                      await nextAuthSignOut({ redirect: false });
                      // Small delay to ensure signOut completes
                      await new Promise(resolve => setTimeout(resolve, 100));
                      window.location.href = '/login';
                    }
                  } catch (error) {
                    console.error('Error during logout:', error);
                    // Fallback: force reload to login anyway
                    window.location.href = '/login';
                  }
                }}
                className="bg-teal-700 hover:bg-teal-800 text-white font-bold px-2 lg:px-4 py-2 rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-teal-400 text-sm"
              >
                <span className="hidden sm:inline">Logout</span>
                <span className="sm:hidden">Out</span>
              </Button>
            </div>
          </div>
        </div>        {/* Page Content */}
        <div className="flex-1 overflow-auto content-background p-4 lg:p-0">
          {children || renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Header;